import React, { useEffect, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import {
  Box, Stack, TextField, Autocomplete, MenuItem, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  useMediaQuery, useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import { AddButton, CancelButton } from "../../../components/mui/Buttons/Buttons";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import Loader3D from "../../../components/Loader/Loader3D";
import {
  addJob,
  updateJob,
  getJobById,
  getJobs,
  getProjects,
  getCourses,
  getBatches,
  getLocations,
  createLocation,
  getFormTemplates,
  clearCourses,
  clearBatches,
} from "../../../redux/features/careerSlice";

const WORK_MODES = ["onsite", "remote", "hybrid"];

// Post/Edit a Job. In CREATE mode it renders its own "Post a Job" button and
// defaults the project to SkillX. In EDIT mode (editJobId + controlled open),
// it fetches the full job and pre-fills every field + the project→course→batches.
const PostJobDialog = ({
  dispatchParams,
  editJobId = null,
  open: openProp,
  setOpen: setOpenProp,
  hideTrigger = false,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { projects, courses, batches, locations, addLoading, formTemplates } = useSelector((state) => state.career);

  // on phones the dialog goes full screen so the long form isn't cramped
  const theme = useTheme();
  // full screen on phones + small tablets (below 900px) so the form isn't squeezed
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const isEdit = Boolean(editJobId);

  // open state: controlled by props when provided (edit), else internal (create)
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = setOpenProp || setInternalOpen;

  const [saving, setSaving] = useState(false);

  // "Add a location" popup. Location is PICK ONLY — typing filters the list but
  // never invents a place — so this is the one way a new one gets in. Anything
  // added here is saved to career_location straight away, so the next officer
  // finds it in the list instead of typing it again their own way.
  const [locationPopup, setLocationPopup] = useState(false);
  const [newLocation, setNewLocation] = useState("");
  const [addingLocation, setAddingLocation] = useState(false);
  // EDIT: true while we fetch the job and fill every field (shows the loader)
  const [prefilling, setPrefilling] = useState(false);

  // cascading targeting selects
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState([]);

  // optional form template + its custom field values ({ field_key: value })
  // (officer fills these — they describe the job's preferences, e.g. gender = male)
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customValues, setCustomValues] = useState({});
  // { field_key: "message" } for required template fields left empty on submit
  const [customErrors, setCustomErrors] = useState({});
  const setCustom = (key, value) => {
    setCustomValues((prev) => ({ ...prev, [key]: value }));
    // clear the field's error as soon as the officer types/picks something
    setCustomErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };
  // EDIT: the template id the job already uses (resolved to selectedTemplate once templates load)
  const [jobTemplateId, setJobTemplateId] = useState(null);

  const defaultedRef = useRef(false); // SkillX default applied? (create only)
  const prefilledRef = useRef(false); // edit prefill done?
  const submitStatusRef = useRef(1); // 0 = save as draft, 1 = publish (create only)

  // set the target status then submit (runs validation via Formik)
  const submitWith = (status) => {
    submitStatusRef.current = status;
    formik.submitForm();
  };

  // a template field counts as empty when it's null/"" or an empty multiselect
  const isBlank = (v) =>
    v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");

  // required fields of the picked template → { field_key: message }
  const validateCustomFields = () => {
    const fields = Array.isArray(selectedTemplate?.fields) ? selectedTemplate.fields : [];
    const errors = {};
    fields.forEach((f) => {
      if (f.is_required && isBlank(customValues[f.field_key])) {
        errors[f.field_key] = `${f.field_label} is required`;
      }
    });
    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      job_title: "", company_name: "", location: "", work_mode: "",
      salary: "", vacancies: "", deadline: "", description: "",
    },
    validationSchema: Yup.object().shape({ job_title: Yup.string().required("Please add a job title") }),
    onSubmit: (values) => {
      if (selectedBatches.length === 0) {
        enqueueSnackbar("Please select at least one target batch", { variant: "warning" });
        return;
      }
      // required fields of the chosen template must be filled before saving
      const fieldErrors = validateCustomFields();
      if (Object.keys(fieldErrors).length) {
        setCustomErrors(fieldErrors);
        enqueueSnackbar("Please fill all required template fields", { variant: "warning" });
        return;
      }
      // drop empty optional fields
      const jobFields = {};
      Object.keys(values).forEach((k) => {
        if (values[k] !== "" && values[k] != null) jobFields[k] = values[k];
      });
      if (jobFields.vacancies != null) jobFields.vacancies = Number(jobFields.vacancies);

      const targets = selectedBatches.map((b) => ({
        course_batch_id: b.course_batch_id,
        project_id: selectedProject?.project_id || null,
        course_available_id: selectedCourse?.course_available_id || null,
      }));

      // attach the chosen form template + the officer's answers (job preferences)
      const templatePart = selectedTemplate
        ? { career_form_template_id: selectedTemplate.career_form_template_id, custom: customValues }
        : {};

      setSaving(true);
      if (isEdit) {
        // edit: keep the current status (don't send job_status)
        dispatch(updateJob({ jobId: editJobId, ...jobFields, targets, ...templatePart })).then((res) => {
          setSaving(false);
          if (res.payload?.status === "success") {
            enqueueSnackbar("Job updated", { variant: "success" });
            dispatch(getJobs(dispatchParams));
            closeDialog();
          } else {
            enqueueSnackbar(res.payload?.message || "Could not update the job", { variant: "error" });
          }
        });
      } else {
        // 0 = save as draft (hidden from students), 1 = publish (live)
        const jobStatus = submitStatusRef.current;
        dispatch(addJob({ ...jobFields, job_status: jobStatus, targets, ...templatePart })).then((res) => {
          setSaving(false);
          if (res.payload?.status === "success") {
            enqueueSnackbar(jobStatus === 0 ? "Draft saved" : "Job posted successfully", { variant: "success" });
            dispatch(getJobs(dispatchParams));
            closeDialog();
          } else {
            enqueueSnackbar(res.payload?.message || "Could not save the job", { variant: "error" });
          }
        });
      }
    },
  });

  const resetTargeting = () => {
    setSelectedProject(null);
    setSelectedCourse(null);
    setSelectedBatches([]);
    setSelectedTemplate(null);
    setCustomValues({});
    setCustomErrors({});
    setJobTemplateId(null);
    dispatch(clearCourses());
    dispatch(clearBatches());
    defaultedRef.current = false;
    prefilledRef.current = false;
  };

  const closeDialog = () => {
    setOpen(false);
    formik.resetForm();
    resetTargeting();
  };

  // load form templates whenever the dialog opens (both create & edit need the picker)
  useEffect(() => {
    if (open) dispatch(getFormTemplates());
  }, [open, dispatch]);

  // the places already used, for the Location suggestions. Loaded on open so a
  // location added by somebody else a minute ago is already in the list.
  useEffect(() => {
    if (open) dispatch(getLocations());
  }, [open, dispatch]);

  // CREATE: load projects when the dialog opens
  useEffect(() => {
    if (open && !isEdit) dispatch(getProjects());
  }, [open, isEdit, dispatch]);

  // CREATE: default the project to SkillX once projects load
  useEffect(() => {
    if (open && !isEdit && projects?.length && !defaultedRef.current) {
      const skillx = projects.find((p) => (p.project_name || "").toLowerCase() === "skillx");
      if (skillx) {
        defaultedRef.current = true;
        setSelectedProject(skillx);
        setSelectedCourse(null);
        setSelectedBatches([]);
        dispatch(clearBatches());
        dispatch(getCourses(skillx.project_id));
      }
    }
  }, [open, isEdit, projects, dispatch]);

  // EDIT: fetch the full job and pre-fill everything when the dialog opens
  useEffect(() => {
    if (!(open && isEdit && !prefilledRef.current)) return;
    prefilledRef.current = true;
    setPrefilling(true); // show the loader until every field is filled
    (async () => {
     try {
      const projRes = await dispatch(getProjects());
      const projList = projRes.payload?.data || [];
      const jobRes = await dispatch(getJobById(editJobId));
      const job = jobRes.payload?.data || {};

      formik.setValues({
        job_title: job.job_title || "",
        company_name: job.company_name || "",
        location: job.location || "",
        work_mode: job.work_mode || "",
        salary: job.salary || "",
        vacancies: job.vacancies != null ? job.vacancies : "",
        deadline: job.deadline || "",
        description: job.description || "",
      });

      // prefill the officer's saved custom answers + remember which template this
      // job uses (selectedTemplate is resolved once the templates list loads below)
      setCustomValues(job.metadata || {});
      setJobTemplateId(job.career_form_template_id || null);

      const targets = job.targets || [];
      if (targets.length) {
        const t0 = targets[0];
        setSelectedProject(projList.find((p) => p.project_id === t0.project_id) || null);
        if (t0.course_available_id) {
          const cRes = await dispatch(getCourses(t0.project_id));
          const course = (cRes.payload?.data || []).find(
            (c) => c.course_available_id === t0.course_available_id
          ) || null;
          setSelectedCourse(course);
          if (course) {
            const bRes = await dispatch(getBatches(t0.course_available_id));
            const ids = new Set(targets.map((t) => t.course_batch_id));
            setSelectedBatches((bRes.payload?.data || []).filter((b) => ids.has(b.course_batch_id)));
          }
        }
      }
     } finally {
      setPrefilling(false); // done — hide the loader, show the filled form
     }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, editJobId]);

  // EDIT: once the templates list is loaded, pick the one the job already uses
  useEffect(() => {
    if (isEdit && jobTemplateId && formTemplates?.length && !selectedTemplate) {
      const t = formTemplates.find((tpl) => tpl.career_form_template_id === jobTemplateId);
      if (t) setSelectedTemplate(t);
    }
  }, [isEdit, jobTemplateId, formTemplates, selectedTemplate]);

  // reset prefill/default flags whenever the dialog closes
  useEffect(() => {
    if (!open) {
      defaultedRef.current = false;
      prefilledRef.current = false;
    }
  }, [open]);

  const onProjectChange = (project) => {
    setSelectedProject(project);
    setSelectedCourse(null);
    setSelectedBatches([]);
    dispatch(clearBatches());
    if (project) dispatch(getCourses(project.project_id));
    else dispatch(clearCourses());
  };

  const onCourseChange = (course) => {
    setSelectedCourse(course);
    setSelectedBatches([]);
    if (course) dispatch(getBatches(course.course_available_id));
    else dispatch(clearBatches());
  };

  // render one custom field (from the chosen template) by its type — the officer
  // fills these to describe the job's preferences; bound to customValues[field_key]
  const renderCustomField = (f) => {
    const label = f.field_label + (f.is_required ? " *" : "");
    const value = customValues[f.field_key] ?? "";
    const opts = Array.isArray(f.field_options) ? f.field_options : [];
    const fieldError = customErrors[f.field_key];
    const commonProps = {
      key: f.career_form_field_id,
      fullWidth: true,
      size: "small",
      label,
      placeholder: f.placeholder || "",
      value,
      onChange: (e) => setCustom(f.field_key, e.target.value),
      error: Boolean(fieldError),
      helperText: fieldError || "",
    };
    switch (f.field_type) {
      case "textarea":
        return <TextField {...commonProps} multiline minRows={2} />;
      case "number":
        return <TextField {...commonProps} type="number" />;
      case "date":
        return <TextField {...commonProps} type="date" InputLabelProps={{ shrink: true }} />;
      case "url":
        return <TextField {...commonProps} type="url" />;
      case "boolean":
        return (
          <TextField {...commonProps} select>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
        );
      case "select":
        return (
          <TextField {...commonProps} select>
            {opts.map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        );
      case "multiselect":
        return (
          <Autocomplete
            key={f.career_form_field_id}
            multiple
            size="small"
            options={opts}
            value={Array.isArray(customValues[f.field_key]) ? customValues[f.field_key] : []}
            onChange={(e, v) => setCustom(f.field_key, v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                error={Boolean(fieldError)}
                helperText={fieldError || ""}
              />
            )}
          />
        );
      default: // text
        return <TextField {...commonProps} />;
    }
  };

  const busy = saving || addLoading;

  /**
   * Add a place from the popup, then select it on the form.
   *
   * Saved to career_location BEFORE the job is, on purpose: the place then
   * exists for everybody, whether or not this job is ever posted, and the next
   * officer picks it instead of typing their own spelling of it.
   *
   * A place that is already on the list is not an error. The server says so,
   * and we just select the one that exists.
   */
  const addLocation = async () => {
    const name = newLocation.trim();
    if (!name) {
      enqueueSnackbar("Type a location first", { variant: "warning" });
      return;
    }
    setAddingLocation(true);
    try {
      const res = await dispatch(createLocation({ location_name: name }));
      const ok = res.payload?.status === "success";
      if (!ok && !String(res.payload?.message || "").includes("already on the list")) {
        enqueueSnackbar(res.payload?.message || "Could not add the location", { variant: "error" });
        return;
      }
      // reload the list so the new place is there to be selected
      const listRes = await dispatch(getLocations());
      const list = listRes.payload?.data || [];
      // the server tidies the spelling (spaces, existing casing), so select the
      // name IT settled on rather than the raw text that was typed
      const saved =
        res.payload?.data?.location_name ||
        list.find((l) => l.location_name.toLowerCase() === name.toLowerCase())?.location_name ||
        name;
      formik.setFieldValue("location", saved);
      enqueueSnackbar(ok ? "Location added" : "Already on the list, selected it", {
        variant: ok ? "success" : "info",
      });
      setLocationPopup(false);
      setNewLocation("");
    } finally {
      setAddingLocation(false);
    }
  };

  return (
    <>
    <GlobalDialog
      title={isEdit ? "Edit Job" : "Post a Job"}
      open={open}
      setOpen={setOpen}
      fullWidth
      fullScreen={fullScreen}
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        hideTrigger ? null : (
          <AddButton size="medium" action={() => setOpen(true)}>
            Post a Job
          </AddButton>
        )
      }
      content={
        prefilling ? (
          // EDIT: while the job is loading, show the 3D loader until every field is filled
          <Box sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader3D />
          </Box>
        ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Box>
              <Stack direction={"column"} spacing={2}>
                <TextField
                  name="job_title"
                  label="Job Title *"
                  value={formik.values.job_title}
                  onChange={formik.handleChange}
                  error={formik.touched.job_title && Boolean(formik.errors.job_title)}
                  helperText={formik.touched.job_title && formik.errors.job_title}
                  fullWidth
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField name="company_name" label="Company" value={formik.values.company_name} onChange={formik.handleChange} fullWidth />
                  {/*
                    Location is PICK ONLY. Typing searches the list; it never
                    creates a place. A new one goes through the + button, which
                    saves it to career_location first and then selects it.

                    That is what keeps "Nilambur", "nilambur" and "Nilambur "
                    from becoming three different places in the filters.
                  */}
                  <Stack direction="row" spacing={1} sx={{ width: "100%" }} alignItems="center">
                    <Autocomplete
                      fullWidth
                      options={locations?.map((l) => l.location_name) || []}
                      // formik holds the NAME, which is what the job is saved with
                      value={formik.values.location || null}
                      onChange={(e, picked) => formik.setFieldValue("location", picked || "")}
                      noOptionsText="Not on the list. Use + to add it."
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="location"
                          label="Location"
                          placeholder="Search a place"
                        />
                      )}
                    />
                    <Tooltip title="Add a location that is not on the list">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setNewLocation("");
                          setLocationPopup(true);
                        }}
                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
                      >
                        <Icon icon="mdi:plus" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField select name="work_mode" label="Work Mode" value={formik.values.work_mode} onChange={formik.handleChange} fullWidth>
                    {/* value stays lowercase (onsite/remote/hybrid) but we show it with a capital first letter */}
                    {WORK_MODES.map((m) => (
                      <MenuItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField name="salary" label="Salary / Stipend" value={formik.values.salary} onChange={formik.handleChange} fullWidth />
                  <TextField name="vacancies" label="Vacancies" type="number" value={formik.values.vacancies} onChange={formik.handleChange} fullWidth />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField name="deadline" label="Application Deadline" type="date" value={formik.values.deadline} onChange={formik.handleChange} InputLabelProps={{ shrink: true }} fullWidth />
                </Stack>

                <TextField name="description" label="Description / Instructions" value={formik.values.description} onChange={formik.handleChange} multiline minRows={2} fullWidth />

                {/* --- Targeting --- */}
                <Typography variant="subtitle2" sx={{ pt: 1 }}>
                  Who is this job for? (pick project → course → batches)
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  {/* project is locked to SkillX for now — only SkillX jobs are
                      posted, so the picker is read-only (defaulted above) */}
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={projects || []}
                    getOptionLabel={(o) => o.project_name || ""}
                    isOptionEqualToValue={(o, v) => o.project_id === v.project_id}
                    value={selectedProject}
                    onChange={(e, v) => onProjectChange(v)}
                    disabled
                    renderInput={(params) => <TextField {...params} label="Project" />}
                  />
                  <Autocomplete
                    sx={{ flex: 1 }}
                    options={courses || []}
                    getOptionLabel={(o) => o.course_name || ""}
                    isOptionEqualToValue={(o, v) => o.course_available_id === v.course_available_id}
                    value={selectedCourse}
                    onChange={(e, v) => onCourseChange(v)}
                    disabled={!selectedProject}
                    renderInput={(params) => <TextField {...params} label="Course" />}
                  />
                </Stack>

                <Autocomplete
                  multiple
                  options={batches || []}
                  getOptionLabel={(o) =>
                    o.course_batch_name + (o.course_batch_serial_no ? ` (${o.course_batch_serial_no})` : "")
                  }
                  isOptionEqualToValue={(o, v) => o.course_batch_id === v.course_batch_id}
                  value={selectedBatches}
                  onChange={(e, v) => setSelectedBatches(v)}
                  disabled={!selectedCourse}
                  renderInput={(params) => (
                    <TextField {...params} label="Target Batches (select one or more)" placeholder="Batches" />
                  )}
                />
              </Stack>

              {/* Optional form template (both create & edit). Picking one shows its
                  fields for the officer to fill — these describe the job's
                  preferences and are saved with the job. */}
              {(
                <Stack direction={"column"} spacing={2} sx={{ pt: 1 }}>
                  <Typography variant="subtitle2">Form Template (optional)</Typography>
                  <Autocomplete
                    size="small"
                    options={formTemplates || []}
                    getOptionLabel={(o) => o.template_name || ""}
                    isOptionEqualToValue={(o, v) =>
                      o.career_form_template_id === v.career_form_template_id
                    }
                    value={selectedTemplate}
                    onChange={(e, v) => {
                      setSelectedTemplate(v);
                      setCustomValues({}); // clear answers when the template changes
                      setCustomErrors({});
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pick a template to add extra fields"
                        placeholder="Template"
                      />
                    )}
                  />
                  {selectedTemplate &&
                    Array.isArray(selectedTemplate.fields) &&
                    selectedTemplate.fields.length > 0 && (
                      <Stack direction={"column"} spacing={2}>
                        {selectedTemplate.fields.map((f) => renderCustomField(f))}
                      </Stack>
                    )}
                </Stack>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                <Stack direction={"row"} spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                  <CancelButton size="small" action={closeDialog}>Cancel</CancelButton>
                  {isEdit ? (
                    <Button variant="contained" size="small" disabled={busy} onClick={() => submitWith(null)}>
                      {busy ? "Please wait…" : "Update Job"}
                    </Button>
                  ) : (
                    <>
                      {/* save hidden from students (status 0) */}
                      <Button variant="outlined" size="small" disabled={busy} onClick={() => submitWith(0)}>
                        {busy ? "Saving…" : "Save as Draft"}
                      </Button>
                      {/* publish live (status 1) */}
                      <Button variant="contained" size="small" disabled={busy} onClick={() => submitWith(1)}>
                        {busy ? "Please wait…" : "Post Job"}
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
        )
      }
    />

    {/*
      Add a location. Sits OUTSIDE the job dialog so it draws on top of it, and
      is the only way a new place gets onto the list from this screen.
    */}
    <Dialog open={locationPopup} onClose={() => setLocationPopup(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Add a location</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          It joins the list for everybody, so check it is not already there under
          a different spelling.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Location"
          placeholder="e.g. Nilambur"
          value={newLocation}
          inputProps={{ maxLength: 120 }}
          onChange={(e) => setNewLocation(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !addingLocation) addLocation();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setLocationPopup(false)} disabled={addingLocation}>
          Cancel
        </Button>
        <Button variant="contained" onClick={addLocation} disabled={addingLocation}>
          {addingLocation ? "Saving…" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default PostJobDialog;
